import React, { useState, useEffect, useCallback } from 'react';
import {
  Clock,
  Download,
  Search,
  XCircle,
  Eye,
  Edit,
  Trash2,
  Loader2,
  FolderOpen,
  ChevronDown,
  ChevronRight,
  FileText,
  Undo2
} from 'lucide-react';
import { formatDateTime } from '../utils/dateFormat';
import { auditApi, AuditEventResponse } from '../services/auditApi';
import { caseApiReal } from '../services/caseApiReal';
import { Case } from '../types';

/** Inline diff display for MODIFY_EVENT / REVERT_EVENT audit entries */
const EventCorrectionDiff: React.FC<{
  previousState: Record<string, any> | null;
  newState: Record<string, any> | null;
}> = ({ previousState, newState }) => {
  if (!previousState && !newState) {
    return <p className="text-xs text-medical-400 italic">No change details recorded for this entry.</p>;
  }

  const fields = ['headline', 'summary', 'description', 'eventDate', 'eventType'];
  const changedFields = fields.filter(f =>
    (previousState?.[f] ?? null) !== (newState?.[f] ?? null)
  );

  const correctionReason = newState?.correctionReason as string | undefined;

  if (changedFields.length === 0 && !correctionReason) {
    return <p className="text-xs text-medical-400 italic">No field changes detected.</p>;
  }

  const labelMap: Record<string, string> = {
    headline: 'Headline',
    summary: 'Summary',
    description: 'Description',
    eventDate: 'Event Date',
    eventType: 'Event Type',
  };

  return (
    <div className="space-y-3">
      {correctionReason && (
        <div className="flex items-center gap-2 text-xs">
          <span className="font-medium text-medical-600 dark:text-medical-400">Reason:</span>
          <span className="text-medical-700 dark:text-medical-300">{correctionReason}</span>
        </div>
      )}
      {changedFields.map(field => (
        <div key={field} className="text-xs">
          <span className="font-medium text-medical-600 dark:text-medical-400 block mb-1">
            {labelMap[field] || field}
          </span>
          <div className="grid grid-cols-2 gap-2">
            <div className="rounded border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-2.5 py-1.5">
              <span className="text-[10px] font-semibold uppercase text-red-500 dark:text-red-400 block mb-0.5">Before</span>
              <span className="text-medical-700 dark:text-medical-300 line-through decoration-red-400/50 break-words">
                {previousState?.[field] != null ? String(previousState[field]) : '—'}
              </span>
            </div>
            <div className="rounded border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 px-2.5 py-1.5">
              <span className="text-[10px] font-semibold uppercase text-emerald-500 dark:text-emerald-400 block mb-0.5">After</span>
              <span className="text-medical-700 dark:text-medical-300 break-words">
                {newState?.[field] != null ? String(newState[field]) : '—'}
              </span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export const AuditPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState('7days');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedCaseId, setSelectedCaseId] = useState<string>('');

  // Data state
  const [auditEvents, setAuditEvents] = useState<AuditEventResponse[]>([]);
  const [cases, setCases] = useState<Case[]>([]);
  const [loading, setLoading] = useState(false);
  const [casesLoading, setCasesLoading] = useState(true);
  const [casesError, setCasesError] = useState<string | null>(null);
  const [auditError, setAuditError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [totalElements, setTotalElements] = useState(0);

  // Expandable diff rows
  const [expandedAuditId, setExpandedAuditId] = useState<string | null>(null);

  // Load user's cases for the filter dropdown
  useEffect(() => {
    const loadCases = async () => {
      try {
        const response = await caseApiReal.getCases({ size: 100 });
        setCases(response.content || []);
        setCasesError(null);
      } catch (err) {
        console.error('Failed to load cases:', err);
        setCasesError('Failed to load cases');
      } finally {
        setCasesLoading(false);
      }
    };
    loadCases();
  }, []);

  // Load audit events when filters change
  const loadAuditEvents = useCallback(async (pageNum: number, append: boolean = false) => {
    setLoading(true);
    try {
      const result = await auditApi.getAllAuditEvents({
        page: pageNum,
        size: 20,
        caseId: selectedCaseId || undefined,
        dateRange,
        search: searchTerm || undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
      });

      setAuditEvents(prev => append ? [...prev, ...result.events] : result.events);
      setHasMore(result.hasMore);
      setTotalElements(result.totalElements);
      setAuditError(null);
    } catch (err) {
      console.error('Failed to load audit events:', err);
      if (!append) {
        setAuditEvents([]);
        setAuditError('Failed to load audit events. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [selectedCaseId, dateRange, searchTerm, statusFilter]);

  useEffect(() => {
    setPage(0);
    loadAuditEvents(0);
  }, [loadAuditEvents]);

  const loadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    loadAuditEvents(nextPage, true);
  };

  // Debounce search
  const [searchDebounce, setSearchDebounce] = useState<NodeJS.Timeout | null>(null);
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    if (searchDebounce) clearTimeout(searchDebounce);
    setSearchDebounce(setTimeout(() => {
      // The useEffect on loadAuditEvents will trigger
    }, 300));
  };

  const formatEventType = (eventType: string) => {
    return eventType
      .replace(/_/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
  };

  const isExpandableAction = (action: string) => {
    const a = action.toUpperCase();
    return a === 'MODIFY_EVENT' || a === 'REVERT_EVENT';
  };

  const getActionIcon = (action: string) => {
    const a = action.toUpperCase();
    if (a.includes('REVERT')) return <Undo2 className="h-4 w-4" />;
    if (a.includes('ACCESS') || a.includes('VIEW')) return <Eye className="h-4 w-4" />;
    if (a.includes('CREATE') || a.includes('ADD') || a.includes('UPLOAD')) return <FileText className="h-4 w-4" />;
    if (a.includes('UPDATE') || a.includes('MODIFY') || a.includes('REORDER')) return <Edit className="h-4 w-4" />;
    if (a.includes('DELETE') || a.includes('REMOVE')) return <Trash2 className="h-4 w-4" />;
    if (a.includes('EXPORT')) return <Download className="h-4 w-4" />;
    return <Clock className="h-4 w-4" />;
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-medical-900 dark:text-medical-100">Activity Log</h1>
        <p className="text-medical-600 dark:text-medical-400">Track all actions performed across your cases</p>
      </div>

      {/* Filters */}
      <div className="card mb-6">
        <div className="flex flex-wrap items-center gap-4">
          {/* Case Filter */}
          <div className="relative min-w-48">
            <FolderOpen className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-medical-400" />
            <select
              value={selectedCaseId}
              onChange={(e) => setSelectedCaseId(e.target.value)}
              className="input-field pl-10 appearance-none pr-8"
              disabled={casesLoading}
            >
              <option value="">All Cases</option>
              {cases.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-medical-400 pointer-events-none" />
          </div>
          <div className="flex-1 min-w-48">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-medical-400" />
              <input
                type="text"
                placeholder="Search activities..."
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="input-field pl-10"
              />
            </div>
          </div>
          <div>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="input-field"
            >
              <option value="1day">Last 24 hours</option>
              <option value="7days">Last 7 days</option>
              <option value="30days">Last 30 days</option>
              <option value="90days">Last 90 days</option>
            </select>
          </div>
          <div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="input-field"
            >
              <option value="all">All Events</option>
              <option value="error">Errors Only</option>
            </select>
          </div>
        </div>
      </div>

      {/* Activity Log */}
      <div className="card">
        {loading && auditEvents.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-primary-500 mr-2" />
            <span className="text-medical-500">Loading audit events...</span>
          </div>
        ) : auditError ? (
          <div className="text-center py-12">
            <XCircle className="h-12 w-12 text-red-400 mx-auto mb-3" />
            <p className="text-red-600 dark:text-red-400 font-medium">Failed to load audit events</p>
            <p className="text-medical-400 text-sm mt-1">
              Please check your connection and try again.
            </p>
            <button
              onClick={() => loadAuditEvents(0)}
              className="btn-primary mt-4"
            >
              Retry
            </button>
          </div>
        ) : auditEvents.length === 0 ? (
          <div className="text-center py-12">
            <Clock className="h-12 w-12 text-medical-300 mx-auto mb-3" />
            <p className="text-medical-500 font-medium">No audit events found</p>
            <p className="text-medical-400 text-sm mt-1">
              {selectedCaseId ? 'No activity recorded for this case yet.' : 'Activity will appear here as you use the system.'}
            </p>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-medical-500">
                Showing {auditEvents.length} of {totalElements} events
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-medical-200 dark:divide-medical-700">
                <thead className="bg-medical-50 dark:bg-medical-900">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-medical-500 uppercase tracking-wider">
                      Timestamp
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-medical-500 uppercase tracking-wider">
                      Action
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-medical-500 uppercase tracking-wider">
                      Case
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-medical-500 uppercase tracking-wider">
                      Details
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-medical-500 uppercase tracking-wider">
                      IP Address
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-medical-800 divide-y divide-medical-200 dark:divide-medical-700">
                  {auditEvents.map((event) => {
                    const expandable = isExpandableAction(event.eventType) && (event.previousState || event.newState);
                    const isExpanded = expandedAuditId === event.id;

                    return (
                      <React.Fragment key={event.id}>
                        <tr
                          className={`hover:bg-medical-50 dark:hover:bg-medical-700 ${event.status === 'error' ? 'bg-red-50 dark:bg-red-900/20' : ''} ${expandable ? 'cursor-pointer' : ''}`}
                          onClick={expandable ? () => setExpandedAuditId(isExpanded ? null : event.id) : undefined}
                        >
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-medical-900 dark:text-medical-100">
                            {formatDateTime(event.createdAt)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-2">
                              {getActionIcon(event.eventType)}
                              <span className="text-sm text-medical-900 dark:text-medical-100">
                                {formatEventType(event.eventType)}
                              </span>
                              {event.status === 'error' && (
                                <XCircle className="h-3.5 w-3.5 text-red-500" />
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-medical-900 dark:text-medical-100">
                            {event.caseName || '-'}
                          </td>
                          <td className="px-6 py-4 text-sm text-medical-500 max-w-xs">
                            <div className="flex items-center gap-1.5">
                              {expandable && (
                                isExpanded
                                  ? <ChevronDown className="h-3.5 w-3.5 flex-shrink-0 text-medical-400" />
                                  : <ChevronRight className="h-3.5 w-3.5 flex-shrink-0 text-medical-400" />
                              )}
                              <span className="truncate">{event.description}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-medical-500">
                            {event.ipAddress || '-'}
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr>
                            <td colSpan={5} className="px-6 py-4 bg-medical-50 dark:bg-medical-900/50 border-b border-medical-200 dark:border-medical-700">
                              <EventCorrectionDiff
                                previousState={event.previousState}
                                newState={event.newState}
                              />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Load More */}
            {hasMore && (
              <div className="text-center mt-4">
                <button
                  onClick={loadMore}
                  disabled={loading}
                  className="btn-secondary inline-flex items-center space-x-2"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                  <span>Load More</span>
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
